import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useRealtorContext } from '../../context/RealtorContext';
import Button from '../ui/Button';
import { motion, AnimatePresence } from 'framer-motion';
import Logou from "../../images/logo.png"

interface NavbarProps {}

const Navbar: React.FC<NavbarProps> = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { currentUser, logout } = useRealtorContext();
  const location = useLocation();
  const navigate = useNavigate();
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if the click is outside the mobile menu AND not on the toggle button itself
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node) &&
          toggleButtonRef.current && !toggleButtonRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]); // Depend on isMenuOpen to add/remove listener

  const handleLogout = () => {
    logout();
    localStorage.removeItem('realtor_token');
    navigate('/');
  };

  const navLinks = [
    { name: 'EcuHome', path: '/', icon: '🏠' },
    { name: 'Home', path: '/home', icon: '�' },
    { name: 'Properties', path: '/properties', icon: '🏘️' },
    { name: 'About', path: '/about', icon: 'ℹ️' },
    { name: 'Contact', path: '/contact', icon: '📞' },
  ];

  const realtorLinks = [
    { name: 'Dashboard', path: '/dashboard', icon: '📊' },
    { name: 'Affiliates', path: '/affiliate/management', icon: '👥' },
    { name: 'Commissions', path: '/commission', icon: '💰' },
    { name: 'Files', path: '/files', icon: '📁' },
  ];

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const isHomePage = location.pathname === '/' || location.pathname === '/home';
  const isDarkBackgroundPage = ['/contact', '/commission', '/files'].includes(location.pathname);

  const navbarBg = (isHomePage && !isScrolled)
    ? 'bg-white/95 backdrop-blur-md border-b border-neutral-200/20 shadow-sm'
    : 'bg-white/95 backdrop-blur-md border-b border-neutral-200/20 shadow-sm';



  return (
    <nav className={`fixed w-full z-50 transition-all duration-500 ${navbarBg}`}>
      <div className="container mx-auto px-4 lg:px-6 xl:px-8 2xl:px-12">
        <div className="flex justify-between items-center py-3 md:py-4">
          {/* Logo */}
           <img src={Logou} style={{width: '70px'}} alt="Unique Solar Gardens Estate" />
          <Link 
            to="/" 
            className="flex items-center group"
            aria-label="Home"
          >
           {/*  <motion.div 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="relative"
            >
              <div className="relative bg-gradient-to-r from-primary-600 to-secondary-500 text-white p-3 rounded-2xl shadow-md group-hover:shadow-lg transition-all duration-300"> */}
                {/* <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 md:h-7 md:w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg> */}
                  
             {/*  </div>
            </motion.div> */}
            <div className="ml-3 md:ml-4">
           
             {/*  <h1 className="text-lg md:text-xl font-display font-bold text-primary-700">
                USGE
              </h1> */}
              {/* <p className="text-xs md:text-sm text-neutral-600">
                E
              </p> */}
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {/* Public Navigation Links */}
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative px-4 py-2 rounded-full font-medium transition-all duration-200 group ${
                  isActive(link.path)
                    ? 'text-primary-600 bg-primary-50 shadow-inner'
                    : (isHomePage && !isScrolled)
                      ? 'text-neutral-700 hover:text-primary-600 hover:bg-primary-50' // Black for homepage transparent
                      : (isDarkBackgroundPage && !isScrolled)
                        ? 'text-[#f5f8fa] hover:text-primary-600 hover:bg-primary-50' // White for dark background pages transparent
                        : 'text-neutral-700 hover:text-primary-600 hover:bg-primary-50' // Default black
                }`}
              >
                <span className="flex items-center gap-2">
                  <span className="text-sm md:hidden">{link.icon}</span>
                  {link.name}
                </span>
                {isActive(link.path) && (
                  <motion.div
                    className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-primary-600 rounded-full"
                    layoutId="activeIndicator"
                  />
                )}
              </Link>
            ))}

            {/* Realtor Links (if logged in) */}
            {currentUser && (
              <>
                <div className="h-6 w-px mx-4 bg-neutral-300" />
                {realtorLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`relative px-4 py-2 rounded-full font-medium transition-all duration-200 group ${
                      isActive(link.path)
                        ? 'text-primary-600 bg-primary-50 shadow-inner'
                        : (isHomePage && !isScrolled)
                          ? 'text-neutral-700 hover:text-primary-600 hover:bg-primary-50' // Black for homepage transparent
                          : (isDarkBackgroundPage && !isScrolled)
                            ? 'text-[#f5f8fa] hover:text-primary-600 hover:bg-primary-50' // White for dark background pages transparent
                            : 'text-neutral-700 hover:text-primary-600 hover:bg-primary-50' // Default black
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-sm md:hidden">{link.icon}</span>
                      {link.name}
                    </span>
                    {isActive(link.path) && (
                      <motion.div
                        className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-primary-600 rounded-full"
                        layoutId="activeIndicator"
                      />
                    )}
                  </Link>
                ))}
              </>
            )}
          </div>

          {/* User Actions */}
          <div className="hidden lg:flex items-center space-x-3">
            {currentUser ? (
              <div className="flex items-center space-x-3">
                <motion.div 
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center space-x-3 px-4 py-2 rounded-full bg-gradient-to-r from-primary-50 to-secondary-50 border border-primary-200/50 cursor-pointer"
                  onClick={() => navigate('/profile')}
                >
                  {currentUser.image ? (
                    <motion.img
                      src={currentUser.image}
                      alt={currentUser.name}
                      className="w-8 h-8 rounded-full object-cover ring-2 ring-primary-200"
                      whileHover={{ scale: 1.1 }}
                      transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    />
                  ) : (
                    <motion.div 
                      className="w-8 h-8 rounded-full bg-gradient-to-r from-primary-600 to-secondary-500 flex items-center justify-center text-white font-medium text-sm"
                      whileHover={{ scale: 1.1 }}
                      transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    >
                      {currentUser.name.charAt(0).toUpperCase()}
                    </motion.div>
                  )}
                  <span className="font-medium text-neutral-700 text-sm">
                    {currentUser.name.split(' ')[0]}
                  </span>
                </motion.div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  }
                >
                  Logout
                </Button>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <Link to="/login">
                  <Button
                    variant="outline"
                    size="sm"
                    icon={
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                      </svg>
                    }
                  >
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button
                    variant="gradient"
                    size="sm"
                    icon={
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                      </svg>
                    }
                  >
                    Join as Realtor
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            ref={toggleButtonRef}
            className="lg:hidden p-2 rounded-full transition-colors duration-200 hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            whileTap={{ scale: 0.9 }}
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6 text-neutral-700"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </motion.button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              ref={mobileMenuRef}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
              className="lg:hidden bg-white border-t border-neutral-200/20 rounded-b-3xl shadow-lg"
            >
              <div className="px-4 py-6 space-y-2">
                {/* Public Navigation Links */}
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-full font-medium transition-all duration-200 ${
                      isActive(link.path)
                        ? 'text-primary-600 bg-primary-50 shadow-inner'
                        : 'text-neutral-700 hover:text-primary-600 hover:bg-primary-50'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <span className="text-lg">{link.icon}</span>
                    {link.name}
                    {isActive(link.path) && (
                      <motion.div 
                        className="ml-auto w-2 h-2 bg-primary-600 rounded-full"
                        layoutId="mobileActiveIndicator"
                      />
                    )}
                  </Link>
                ))}

                {/* Realtor Links (if logged in) */}
                {currentUser && (
                  <>
                    <div className="border-t border-neutral-200 pt-4 mt-4">
                      <p className="text-sm font-medium text-neutral-500 mb-2 px-4">Realtor Portal</p>
                      {realtorLinks.map((link) => (
                        <Link
                          key={link.path}
                          to={link.path}
                          className={`flex items-center gap-3 px-4 py-3 rounded-full font-medium transition-all duration-200 ${
                            isActive(link.path)
                              ? 'text-primary-600 bg-primary-50 shadow-inner'
                              : 'text-neutral-700 hover:text-primary-600 hover:bg-primary-50'
                          }`}
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <span className="text-lg">{link.icon}</span>
                          {link.name}
                          {isActive(link.path) && (
                            <motion.div 
                              className="ml-auto w-2 h-2 bg-primary-600 rounded-full"
                              layoutId="mobileActiveIndicator"
                            />
                          )}
                        </Link>
                      ))}
                    </div>
                  </>
                )}

                {/* User Actions */}
                <div className="border-t border-neutral-200 pt-4 mt-4">
                  {currentUser ? (
                    <div className="space-y-3">
                      <div 
                        className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-full cursor-pointer"
                        onClick={() => {
                          navigate('/profile');
                          setIsMenuOpen(false);
                        }}
                      >
                        {currentUser.image ? (
                          <img
                            src={currentUser.image}
                            alt={currentUser.name}
                            className="w-10 h-10 rounded-full object-cover ring-2 ring-primary-200"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-primary-600 to-secondary-500 flex items-center justify-center text-white font-medium">
                            {currentUser.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-neutral-700">{currentUser.name}</p>
                          <p className="text-sm text-neutral-500">{currentUser.role}</p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        fullWidth
                        onClick={() => {
                          handleLogout();
                          setIsMenuOpen(false);
                        }}
                        icon={
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                        }
                      >
                        Logout
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                        <Button variant="outline" size="sm" fullWidth>
                          Login
                        </Button>
                      </Link>
                      <Link to="/register" onClick={() => setIsMenuOpen(false)}>
                        <Button variant="gradient" size="sm" fullWidth>
                          Join as Realtor
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navbar;